import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import { useCreateGrow, useGrowsQuery } from "../hooks/useGrowsQuery.js";
import DeviceSelect from "../components/DeviceSelect.jsx";
import { useInventoryQuery } from "../hooks/useInventoryQuery.js";
import usePlan from "../hooks/usePlan.js";
import Button from "../components/ui/Button.jsx";
import Input from "../components/ui/Input.jsx";
import Select from "../components/ui/Select.jsx";
import Modal from "../components/ui/Modal.jsx";

const PLAN_LIMITS = { hobby: 1, pro: 10, enterprise: Infinity };

function UpgradeModal({ current, limit, onClose }) {
  return (
    <Modal open onClose={onClose} title="Grow limit reached">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        You've reached your Hobby plan limit ({current}/{limit} grows).<br />
        To upgrade to Pro, email us at{" "}
        <a href="mailto:hello@fungistics.com" className="text-sky-700 dark:text-sky-400 underline">hello@fungistics.com</a>.
      </p>
      <Button onClick={onClose} className="w-full">Got it</Button>
    </Modal>
  );
}

const MUSHROOM_SPECIES = [
  "Vader (Psilocybe cubensis)",
  "Makilla Gorilla (Psilocybe cubensis)",
  "Trinity (Psilocybe cubensis)",
  "African Transkei (Psilocybe cubensis)",
  "Albedo (Psilocybe cubensis)",
  "Tidal Wave (Psilocybe cubensis)",
  "Super Strain Koh Samui (Psilocybe cubensis)",
  "Hillbilly (Psilocybe cubensis)",
  "PE6 (Psilocybe cubensis)",
  "Bluey Vuitton (Psilocybe cubensis)",
  "Pleurotus ostreatus",
  "Pleurotus eryngii",
  "Lentinula edodes",
  "Grifola frondosa",
  "Hericium erinaceus",
  "Agaricus bisporus",
];

const SUBSTRATES = [
  "Straw",
  "Sawdust",
  "Masters Mix",
  "Soy Hull Pellets",
  "Cardboard",
  "Coffee Grounds",
  "Coco Coir",
  "Manure/Compost",
];

export default function GrowNew() {
  const navigate = useNavigate();
  const createGrow = useCreateGrow();
  const { data: inventoryItems } = useInventoryQuery();
  const { plan, planLoading } = usePlan();
  const { data: grows } = useGrowsQuery();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const [name, setName] = useState("");
  const [speciesInput, setSpeciesInput] = useState("");
  const [substrate, setSubstrate] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [inventoryItemId, setInventoryItemId] = useState("");
  const [error, setError] = useState(null);

  const growCount = grows?.length ?? 0;
  const limit = PLAN_LIMITS[plan ?? "hobby"] ?? 1;
  const atLimit = !planLoading && growCount >= limit;

  const readyItems = (inventoryItems ?? []).filter((i) => i.status === "ready");
  const selectedItem = readyItems.find((i) => i.id === Number(inventoryItemId)) ?? null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const body = {
      name: name.trim(),
      crop_type: "mushroom",
    };
    if (speciesInput.trim()) body.species = speciesInput.trim();
    if (substrate) body.substrate = substrate;
    if (deviceId) body.device_id = Number(deviceId);
    if (inventoryItemId) body.inventory_item_id = Number(inventoryItemId);

    try {
      const grow = await createGrow.mutateAsync(body);
      navigate(`/grows/${grow.id}`);
    } catch (err) {
      setError(err.message ?? "Failed to create grow");
    }
  }

  return (
    <LayoutWide title="New Grow">
      {showUpgrade && (
        <UpgradeModal current={growCount} limit={limit} onClose={() => setShowUpgrade(false)} />
      )}
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/grows")}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Back"
          >
            ←
          </button>
          <h1 className="text-2xl font-semibold text-sky-800 dark:text-sky-300">New Grow</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Grow name <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <Input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Oyster batch #4"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Species
            </label>
            <Input
              type="text"
              list="species-list"
              value={speciesInput}
              onChange={(e) => setSpeciesInput(e.target.value)}
              placeholder="e.g. Pleurotus ostreatus"
            />
            <datalist id="species-list">
              {MUSHROOM_SPECIES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Substrate
            </label>
            <Select value={substrate} onChange={(e) => setSubstrate(e.target.value)}>
              <option value="">— select substrate —</option>
              {SUBSTRATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Use from inventory
            </label>
            {readyItems.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">No inventory items with status "ready".</p>
            ) : (
              <Select value={inventoryItemId} onChange={(e) => setInventoryItemId(e.target.value)}>
                <option value="">— enter manually —</option>
                {readyItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}{item.species ? ` · ${item.species}` : ""} ({item.quantity} {item.unit} remaining)
                  </option>
                ))}
              </Select>
            )}
            {selectedItem && (
              <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-400">
                1 {selectedItem.unit.replace(/s$/, "")} will be deducted from <span className="font-medium">{selectedItem.name}</span> ({selectedItem.quantity} → {selectedItem.quantity - 1} remaining)
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Monitor device
            </label>
            <DeviceSelect value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Link a sensor device to track live environmental data for this grow.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {atLimit && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-400">
              You've reached your {plan === "hobby" ? "Hobby" : "Pro"} plan limit ({growCount}/{limit} grows).{" "}
              <button type="button" onClick={() => setShowUpgrade(true)} className="underline font-medium">Learn more</button>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type={atLimit ? "button" : "submit"}
              onClick={atLimit ? () => setShowUpgrade(true) : undefined}
              disabled={(!atLimit && (createGrow.isPending || !name.trim())) || planLoading}
              className="flex-1"
            >
              {planLoading ? "Loading…" : atLimit ? "Plan limit reached" : createGrow.isPending ? "Creating…" : "Create grow"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/grows")}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </LayoutWide>
  );
}
